import {GitHubAvatar} from '@github-ui/github-avatar'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {AlertIcon, ChevronDownIcon, ChevronRightIcon, InfoIcon, StopIcon} from '@primer/octicons-react'
import {Button, IconButton, Text, Truncate} from '@primer/react'
import {clsx} from 'clsx'

import type {DiffAnnotation} from '../../conversation-types'
import {DiffAnnotationLevels} from '../../conversation-types'
import styles from './Annotation.module.css'
import {AnnotationPresentationMap} from './AnnotationIcon'
import {useEffect, useMemo, useState, type PropsWithChildren} from 'react'
import safeStorage from '@github-ui/safe-storage'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

export const IconMap = {
  [DiffAnnotationLevels.Failure]: <StopIcon className="fgColor-danger mr-2" />,
  [DiffAnnotationLevels.Warning]: <AlertIcon className="fgColor-attention mr-2" />,
  [DiffAnnotationLevels.Notice]: <InfoIcon className="fgColor-muted mr-2" />,
}

/**
 * A check annotation
 * Preferred method is to pass in ghostUser from the app payload
 * Because this is a shared package, ghostUser is optional and will default to constants
 */
export function Annotation({annotation, inSidePanel}: {annotation: DiffAnnotation; inSidePanel?: boolean}) {
  const AnnotationHeadingTag = useMemo(() => (inSidePanel ? 'h3' : 'h2'), [inSidePanel])
  const {isCollapsed, handleToggleCollapsed} = useAnnotationCollapseState(annotation)
  return (
    <div
      className={clsx(
        'p-1 bgColor-default',
        AnnotationPresentationMap[annotation.annotationLevel].borderColor,
        styles.annotation,
      )}
    >
      <div className="d-flex flex-items-center">
        {!inSidePanel && (
          <IconButton
            aria-label={isCollapsed ? 'Expand annotation' : 'Collapse annotation'}
            icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
            size="small"
            variant="invisible"
            onClick={handleToggleCollapsed}
          />
        )}
        {!inSidePanel && <AnnotationBanner annotation={annotation} />}
        {inSidePanel && <SidePanelAnnotationBanner annotation={annotation} />}
      </div>
      {(!isCollapsed || inSidePanel) && (
        <>
          <div className="ml-4 mt-2">
            {annotation.title && (
              <Text
                weight="semibold"
                size="medium"
                className={AnnotationPresentationMap[annotation.annotationLevel].primaryColorClass}
              >
                {annotation.title}
              </Text>
            )}
          </div>
          <AnnotationHeadingTag className="sr-only">
            {AnnotationPresentationMap[annotation.annotationLevel].ariaLabel}: {annotation.title}
          </AnnotationHeadingTag>
          <div className="mb-3 mt-1 ml-4">
            {/* Message is sanitized from the server, see PullRequests::PageData::Annotations::Loader */}
            {annotation.message && (
              <VerifiedHTMLDiv html={annotation.message as SafeHTMLString} className={styles.annotationMessage} />
            )}
          </div>
          <div className="d-flex flex-items-center flex-justify-between ml-4 pr-2 pb-2">
            <div className={styles.annotationSource}>
              <GitHubAvatar src={annotation.appAvatarUrl} alt={annotation.appAvatarAltText} />
              <Truncate
                className="ml-2"
                maxWidth="300px"
                inline
                title={`${annotation.checkSuiteName} / ${annotation.checkRun.name}`}
              >
                <Text weight="semibold" size="small">
                  {annotation.checkSuiteName}
                </Text>{' '}
                / <Text size="small">{annotation.checkRun.name}</Text>
              </Truncate>
            </div>
            {annotation.checkRun.detailsUrl && (
              <Button as="a" href={annotation.checkRun.detailsUrl} size="small">
                View details
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function Emphasis({children}: PropsWithChildren) {
  return <span className="text-semibold fgColor-default">{children}</span>
}

function AnnotationBanner({annotation}: {annotation: DiffAnnotation}) {
  const diffSideLetter = 'R'
  return (
    <h2 className={styles.inlineReviewThreadHeading}>
      {IconMap[annotation.annotationLevel]}
      {AnnotationPresentationMap[annotation.annotationLevel].ariaLabel} on line{' '}
      <Emphasis>
        {diffSideLetter}
        {annotation.endLine}
      </Emphasis>
    </h2>
  )
}

function SidePanelAnnotationBanner({annotation}: {annotation: DiffAnnotation}) {
  return (
    <div className={clsx(styles.inlineReviewThreadHeading, 'ml-4', 'pt-1', 'pl-0')}>
      <Emphasis>{AnnotationPresentationMap[annotation.annotationLevel].ariaLabel}</Emphasis>
    </div>
  )
}

export function createAnnotationLocalStorageKey(annotation: DiffAnnotation) {
  return `AnnotationIsCollapsed_${annotation.path}_${annotation.endLine}_${annotation.annotationLevel}_${annotation.title}`
}

export function useAnnotationCollapseState(annotation: DiffAnnotation) {
  const safeLocalStorage = safeStorage('localStorage')

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

  useEffect(() => {
    const storedState = localStorage.getItem(createAnnotationLocalStorageKey(annotation))
    if (storedState !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsCollapsed(JSON.parse(storedState))
    }
  }, [annotation])

  const handleToggleCollapsed = () => {
    ssrSafeWindow?.requestAnimationFrame(() => {
      safeLocalStorage.setItem(createAnnotationLocalStorageKey(annotation), JSON.stringify(!isCollapsed))
    })
    setIsCollapsed((prevIsCollapsed: boolean) => !prevIsCollapsed)
  }
  return {isCollapsed, handleToggleCollapsed}
}

try{ Annotation.displayName ||= 'Annotation' } catch {}
try{ AnnotationHeadingTag.displayName ||= 'AnnotationHeadingTag' } catch {}
try{ Emphasis.displayName ||= 'Emphasis' } catch {}
try{ AnnotationBanner.displayName ||= 'AnnotationBanner' } catch {}
try{ SidePanelAnnotationBanner.displayName ||= 'SidePanelAnnotationBanner' } catch {}